import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ChoiceButton } from '@/components/bookings/dialogKit'
import { NewBookingDialog } from '@/components/bookings/NewBookingDialog'
import { BlockTimeDialog } from '@/components/calendar/BlockTimeDialog'
import { CalendarNoteDialog } from '@/components/calendar/CalendarNoteDialog'
import { useCopiedClient } from '@/contexts/CopiedClientContext'
import { fmtDayDate } from '@/lib/format'
import { minutesToLabel, type SlotPick } from '@/lib/slotBooking'

// ─── What a click on empty calendar time offers ────────────────────────────────
//
// Three things can go in an empty slot, and they are genuinely different
// decisions rather than variations of one:
//
//   Book       — sells the time to a customer
//   Block      — takes the time off the market entirely
//   Note       — leaves a memo and changes nothing about the time
//
// So the click opens a menu first rather than assuming the most common one. Each
// item is a whole decision with no arguments, which is the rule that keeps the
// calendar's write surface from turning into a form; the arguments live inside
// the dialog each item opens, and each of those states its own containing rule.
//
// ONE INSTANCE, held at the renderer. Deliberately not a Radix menu per band: a
// day grid draws roughly thirty bands per machine column, and mounting a menu on
// each would put hundreds of them in the tree to serve one click.

type SlotAction = 'menu' | 'book' | 'block' | 'note'

export interface SlotActionsProps {
  /** The clicked slot, or null when nothing is open. */
  pick: SlotPick | null
  /** Called with false when the whole flow closes. */
  onOpenChange: (open: boolean) => void
  /** Refetch the calendar after any of the three writes lands. */
  onChanged?: () => void
}

export function SlotActions({ pick, onOpenChange, onChanged }: SlotActionsProps) {
  const [action, setAction] = useState<SlotAction>('menu')
  // Set only by "Paste", so the booking dialog can skip the customer search.
  const [pasteRequested, setPasteRequested] = useState(false)
  const { client: copied } = useCopiedClient()

  // Every fresh click starts at the menu. Without this, choosing Block once would
  // make the next empty-slot click open Block directly.
  useEffect(() => {
    if (pick === null) return
    setAction('menu')
    setPasteRequested(false)
  }, [pick?.date, pick?.startMinutes, pick?.resourceId, pick])

  const close = () => onOpenChange(false)

  const menuOpen = pick !== null && action === 'menu'
  const subPick = (which: SlotAction) => (pick !== null && action === which ? pick : null)

  return (
    <>
      <Dialog open={menuOpen} onOpenChange={(next) => { if (!next) close() }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {pick ? minutesToLabel(pick.startMinutes) : ''}
              {pick?.resourceName ? ` · ${pick.resourceName}` : ''}
            </DialogTitle>
            <DialogDescription>{pick ? fmtDayDate(pick.date) : ''}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <ChoiceButton
              title="New booking"
              subtitle="Pick a client, service and time"
              onClick={() => setAction('book')}
            />

            {/* Only when somebody has actually been copied. An always-present
                "Paste" that does nothing is worse than no button — the operator
                cannot tell whether the copy worked. */}
            {copied ? (
              <ChoiceButton
                title={`Paste ${copied.name}`}
                subtitle="Book them again here — no need to search"
                onClick={() => { setPasteRequested(true); setAction('book') }}
              />
            ) : null}

            <ChoiceButton
              title="Block time"
              subtitle={
                pick?.resourceId === null
                  ? 'Close the whole centre — nothing bookable'
                  : `Take ${pick?.resourceName ?? 'this resource'} out of service`
              }
              onClick={() => setAction('block')}
            />

            <ChoiceButton
              title="Add a note"
              subtitle="A memo for the day — the slot stays bookable"
              onClick={() => setAction('note')}
            />
          </div>

          <p className="text-center text-[11px] text-muted-foreground">
            Anything else — a price change, a package, a new no-show — ask in chat.
          </p>
        </DialogContent>
      </Dialog>

      <NewBookingDialog
        pick={subPick('book')}
        presetCustomer={pasteRequested && copied ? { id: copied.id, name: copied.name, phone: null } : null}
        onOpenChange={(open) => { if (!open) close() }}
        onCreated={onChanged}
      />

      <BlockTimeDialog
        pick={subPick('block')}
        onOpenChange={(open) => { if (!open) close() }}
        onCreated={onChanged}
      />

      <CalendarNoteDialog
        pick={subPick('note')}
        onOpenChange={(open) => { if (!open) close() }}
        onCreated={onChanged}
      />
    </>
  )
}

/** Re-exported so the calendar views can type their callback without a deep import. */
export type { SlotPick }
